import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import strings from 'strings';
import { TextBox, When } from 'UI/components/atoms';
import CustomDatePicker from 'UI/components/atoms/CustomDatePicker';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPToggleButton from 'UI/components/molecules/FPToggleButton';
import { REQUIRED_VALIDATION } from 'UI/utils';

import { FORM_FIELDS_MAP } from '../../drawers/CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

import { FixedHourFields, TimeRangeFields } from './components';
import { TIME_FRAME_MAP } from './InterviewSchedule.constants';
import { useStyles } from './InterviewSchedule.styled';

const STRINGS = strings.sendoutsV2.form.sections.interviewDetails;

const TIME_FRAME_OPTIONS = Object.values(TIME_FRAME_MAP);
const INTERVIEW_TYPE_OPTIONS = {
  RemoteVideo: {
    value: 'remoteVideo',
    title: STRINGS.labels.remoteVideo,
    icon: <VideocamOutlinedIcon />
  },
  FaceToFace: {
    value: 'faceToFace',
    title: STRINGS.labels.faceToFace,
    icon: <LocationOnOutlinedIcon />
  }
};

export const InterviewSchedule = () => {
  const classes = useStyles();
  const { control, errors, setValue, watch } = useFormContext();

  const formValues = watch([
    FORM_FIELDS_MAP.InterviewDate.key,
    FORM_FIELDS_MAP.InterviewTimeFrame.key,
    FORM_FIELDS_MAP.InterviewType.key
  ]);

  const interviewDate = formValues[FORM_FIELDS_MAP.InterviewDate.key];

  const handleToggleChange = value => {
    setValue(FORM_FIELDS_MAP.InterviewType.key, value, true);
  };

  const handleDateChange = (name, value) => {
    setValue(name, value, true);
  };

  const handleControllerChange = useCallback(([, value]) => value, []);

  return (
    <>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.InterviewType.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={INTERVIEW_TYPE_OPTIONS.RemoteVideo.value}
          as={
            <FormControl component="fieldset" className={classes.toggleGroupFullWidth}>
              <FPToggleButton
                onChange={handleToggleChange}
                options={Object.values(INTERVIEW_TYPE_OPTIONS)}
                initialValue={INTERVIEW_TYPE_OPTIONS.RemoteVideo.value || null}
                size="large"
              />
            </FormControl>
          }
        />
      </Grid>
      <Grid item container spacing={1}>
        <Grid item xs={6}>
          <Controller
            name={FORM_FIELDS_MAP.InterviewDate.key}
            rules={REQUIRED_VALIDATION}
            defaultValue={null}
            control={control}
            as={
              <CustomDatePicker
                disablePast
                isClearable
                name={FORM_FIELDS_MAP.InterviewDate.key}
                error={!!errors[FORM_FIELDS_MAP.InterviewDate.key]}
                helperText={errors[FORM_FIELDS_MAP.InterviewDate.key]?.message}
                label={STRINGS.labels.date || null}
                value={interviewDate}
                onDateChange={handleDateChange}
                placeholder={STRINGS.labels.date}
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name={FORM_FIELDS_MAP.InterviewTimeFrame.key}
            control={control}
            rules={REQUIRED_VALIDATION}
            defaultValue={null}
            as={
              <AutocompleteSelect
                error={!!errors[FORM_FIELDS_MAP.InterviewTimeFrame.key]}
                errorText={errors[FORM_FIELDS_MAP.InterviewTimeFrame.key]?.message}
                placeholder={STRINGS.labels.timeFrame}
                options={TIME_FRAME_OPTIONS}
              />
            }
            onChange={handleControllerChange}
            onChangeName="onSelect"
            valueName="selectedValue"
          />
        </Grid>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewTimeFrame.key]?.id === TIME_FRAME_MAP.FixedHour.id
          }
        >
          <FixedHourFields />
        </When>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewTimeFrame.key]?.id === TIME_FRAME_MAP.TimeRange.id
          }
        >
          <TimeRangeFields />
        </When>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewType.key] ===
            INTERVIEW_TYPE_OPTIONS.FaceToFace.value
          }
        >
          <Grid item xs={12}>
            <Controller
              name={FORM_FIELDS_MAP.InterviewAddress.key}
              control={control}
              defaultValue=""
              as={
                <TextBox
                  label={STRINGS.labels.interviewAddress}
                  placeholder={STRINGS.labels.interviewAddress}
                />
              }
              onChange={handleControllerChange}
              onChangeName="onChange"
              valueName="value"
            />
          </Grid>
        </When>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewType.key] ===
            INTERVIEW_TYPE_OPTIONS.RemoteVideo.value
          }
        >
          <Grid item xs={12}>
            <Controller
              name={FORM_FIELDS_MAP.InterviewLink.key}
              defaultValue=""
              control={control}
              as={
                <TextBox
                  label={STRINGS.labels.interviewLink}
                  placeholder={STRINGS.labels.interviewLink}
                />
              }
              onChange={handleControllerChange}
              onChangeName="onChange"
              valueName="value"
            />
          </Grid>
        </When>
      </Grid>
    </>
  );
};
